// src/rms/pages/Settings.js
// ============================================================================
// Settings — configuration, plan and staff
// ============================================================================
// The plan tab is the freemium gate. plan_tier and ads_enabled move together,
// which is the single condition every ad slot checks. There is no per-slot
// opt-out, so the paid tier cannot leak an ad.
//
// Those two columns are service_role-only on the server, so this screen cannot
// grant the paid tier -- that will be the billing webhook's job. On the local
// backend there is no database and no billing, so the toggle still works there
// and remains the way to preview both states.
// ============================================================================

import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import db, { isLocalBackend } from "../data";
import { useRms } from "../context/RmsContext";
import { useCollection } from "../hooks/useCollection";
import { resetStore } from "../data/localAdapter";
import seedOperator from "../data/seed";
import { countDemoRows, deleteDemoData } from "../data/demoData";
import { supabase } from "../config/supabaseClient";
import {
  EXPORT_COLLECTIONS,
  exportOperatorJson,
  exportOperatorCsv,
} from "../data/exportService";
import { removeOperatorScans } from "../data/documentStorage";
import { PageHeader, Field, TextInput, Select, Banner, StatusPill, formatDate } from "../components/ui";
import adEngine from "../ads/adEngine";
import memberService from "../data/memberService";
import pinService from "../data/pinService";
import PinModal from "../components/PinModal";
import { useElevation } from "../components/PinGate";
import { ROLE_OPTIONS } from "../data/permissions";
import SettingsTax from "./SettingsTax";
import SettingsHours from "./SettingsHours";
import SettingsAgreements from "./SettingsAgreements";
import { loadTaxConfig } from "../data/taxService";

/**
 * The free plan's bullets. Data for the same reason the landing page's are:
 * four <li> elements are four chances to forget a t() call.
 */
const PLAN_AD_FREE = [
  "Everything in Free",
  "No ads anywhere",
  "Instant page changes — no reload between sections",
  "Priority support",
];

const PLAN_FREE = [
  "Every feature, no limits",
  "Unlimited staff",
  "Sidebar and banner ads",
  "Never during checkout or payment",
];

// `capability` filters the tab out entirely for somebody who cannot use it.
// Gating the ROUTE instead would only bounce the URL and leave the tab sitting
// there to be clicked, since this whole page is one component behind :tab.
//
// Courtesy, not security. Every one of these has a counterpart in RLS, and a
// staff member who edits this array reaches a screen whose saves are refused.
const TABS = [
  { key: "general", label: "General" },
  // Beside General rather than under it: when the counter is open is a fact
  // about the business, of the same kind as its name and its currency.
  { key: "hours", label: "Hours" },
  { key: "tax", label: "Tax", capability: "manageTax" },
  // Owner only, and narrower than Tax: the contract decides who pays for a
  // wrecked vehicle, which is not a bookkeeping question. Mirrors
  // rms_can_manage_agreements, and the writes behind it are refused by Postgres
  // for anybody else -- this line only decides whether the tab is worth showing.
  { key: "agreements", label: "Agreements", labelKey: "settings.tab.agreements", capability: "manageAgreements" },
  { key: "plan", label: "Plan & ads", capability: "changePlan" },
  { key: "staff", label: "Staff" },
  { key: "data", label: "Data", capability: "exportData" },
];

function Settings() {
  const { tab = "general" } = useParams();
  const navigate = useNavigate();
  const { operator, config, t, memberLoading, updateOperator, invalidate } = useRms();

  // The PIN that opened this section decides which tabs appear, NOT the session
  // that happens to be signed in. On a counter terminal those are different
  // people -- the terminal is staff-level all day, and the owner who just typed
  // their PIN would otherwise find Tax, Plan and Data missing from their own
  // settings. Outside a gate this falls back to the session's own rights.
  const { can } = useElevation();

  const [notice, setNotice] = useState(null);
  // Staff keeps its tab for everyone: a member who cannot manage the roster can
  // still see who their colleagues are, which is what the SELECT policy has
  // always allowed. The tab explains the restriction rather than hiding it.
  const visibleTabs = TABS.filter((x) => !x.capability || can[x.capability]);

  // The fallback below is why this waits for the role rather than filtering
  // optimistically: while `can` is still empty, a direct link to
  // /rms/settings/tax finds no matching visible tab and silently lands on
  // General instead -- so the address bar and the screen disagree, and the
  // screen wins. Same race that was bouncing /billing to the dashboard.
  const active = visibleTabs.some((x) => x.key === tab) ? tab : "general";

  // Most notices are plain success strings. Anything that can fail passes
  // { tone, text } instead, so a refusal is not painted green.
  const banner = typeof notice === "string" ? { tone: "ok", text: notice } : notice;

  // Tax spans four tables, so it is loaded once here and handed down rather
  // than re-read by each section of the screen.
  const [taxConfig, setTaxConfig] = useState(null);
  const reloadTax = useCallback(async () => {
    const [config, categoryRows] = await Promise.all([
      loadTaxConfig(operator),
      db.list("taxCategoryDefaults", { operator_id: operator.id }),
    ]);
    setTaxConfig({ ...config, categoryRows: categoryRows.data || [] });
  }, [operator]);

  useEffect(() => {
    if (active === "tax") reloadTax();
  }, [active, reloadTax]);

  return (
    <>
      <PageHeader title={t("settings.settings", "Settings")} subtitle={`${operator.business_name} · ${config.label}`} />

      {memberLoading ? (
        <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)" }}>
          {t("common.loading", "Loading…")}
        </p>
      ) : (
      <>
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {visibleTabs.map((x) => (
          <button
            key={x.key}
            type="button"
            className={`rms-btn ${active === x.key ? "rms-btn--primary" : "rms-btn--ghost"} rms-btn--sm`}
            onClick={() => navigate(`/rms/settings/${x.key}`)}
          >
            {x.labelKey ? t(x.labelKey, x.label) : x.label}
          </button>
        ))}
      </div>

      {banner ? <Banner tone={banner.tone}>{banner.text}</Banner> : null}

      <div>
        {active === "general" ? <GeneralTab operator={operator} updateOperator={updateOperator} setNotice={setNotice} /> : null}
        {active === "tax" ? (
          taxConfig ? (
            <SettingsTax
              operator={operator}
              taxConfig={taxConfig}
              updateOperator={updateOperator}
              reload={reloadTax}
              setNotice={setNotice}
            />
          ) : (
            <p style={{ color: "var(--rms-muted-strong)" }}>{t("common.loading", "Loading…")}</p>
          )
        ) : null}
        {active === "hours" ? <SettingsHours onNotice={setNotice} /> : null}
        {active === "agreements" ? <SettingsAgreements operator={operator} setNotice={setNotice} /> : null}
        {active === "plan" ? <PlanTab operator={operator} updateOperator={updateOperator} setNotice={setNotice} /> : null}
        {active === "staff" ? <StaffTab operator={operator} invalidate={invalidate} /> : null}
        {active === "data" ? <DataTab operator={operator} setNotice={setNotice} /> : null}
      </div>
      </>
      )}
    </>
  );
}

// ----------------------------------------------------------------------------

function GeneralTab({ operator, updateOperator, setNotice }) {
  const { t } = useRms();
  const [name, setName] = useState(operator.business_name);
  const [currency, setCurrency] = useState(operator.currency);

  const save = async () => {
    adEngine.recordAction();
    await updateOperator({ business_name: name, currency });
    setNotice("Business details saved.");
  };

  return (
    <section className="rms-card rms-card-pad">
      <h2 className="rms-section-title">{t("settings.business.title", "Business details")}</h2>
      <div className="rms-field-grid">
        <Field label={t("settings.business.name", "Business name")}>
          <TextInput value={name} onChange={setName} />
        </Field>
        <Field label={t("settings.business.currency", "Currency")}>
          <TextInput value={currency} onChange={setCurrency} />
        </Field>
      </div>
      <button type="button" className="rms-btn rms-btn--primary" onClick={save}>
        {t("action.save", "Save")}
      </button>
    </section>
  );
}

function PlanTab({ operator, updateOperator, setNotice }) {
  const { t } = useRms();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const isAdFree = operator.plan_tier === "ad_free";

  // No ad telemetry is read here any more, and that is the point rather than a
  // consequence of removing the tables above: this screen used to pull the
  // operator's whole impression and click history into the browser to compute
  // revenue and RPM they earn nothing from. Not fetching it is the difference
  // between hiding the numbers and not gathering them in the first place.

  const switchPlan = async (tier) => {
    // The plan columns belong to billing, not to the browser. Since
    // 20260817140000_rms_lock_billing_columns.sql only service_role may write
    // them, so that an operator cannot simply hand themselves the paid tier.
    // Saying so is better than sending a write that will be refused and
    // reporting success anyway, which is what this did before.
    if (!isLocalBackend) {
      setNotice({
        tone: "info",
        text: "Changing plan goes through billing, which is not connected yet. Nothing was changed.",
      });
      return;
    }

    setBusy(true);
    adEngine.recordAction();
    // plan_tier and ads_enabled move together - the database CHECK constraint
    // enforces the same pairing server-side.
    const result = await updateOperator({
      plan_tier: tier,
      ads_enabled: tier === "free",
      plan_started_at: new Date().toISOString(),
    });
    setBusy(false);

    if (!result.success) {
      setNotice({ tone: "danger", text: result.error || "The plan could not be changed." });
      return;
    }

    setNotice(tier === "ad_free" ? "Ads are off. Enjoy the quiet." : "Switched back to the free, ad-supported plan.");
  };

  return (
    <>
      <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
        <h2 className="rms-section-title">{t("settings.plan.title", "Your plan")}</h2>
        <div className="rms-price-grid" style={{ maxWidth: "none" }}>
          <div className={`rms-price-card${!isAdFree ? " is-featured" : ""}`}>
            <strong style={{ fontSize: 14 }}>{t("plan.freeName", "Free")}</strong>
            <div className="rms-price-amount">$0</div>
            <div style={{ fontSize: 13, color: "var(--rms-muted-strong)" }}>{t("settings.plan.freeTag", "ad-supported")}</div>
            <ul className="rms-price-list">
              {PLAN_FREE.map((line, i) => (
                <li key={i}>{t(`settings.plan.free.${i}`, line)}</li>
              ))}
            </ul>
            {isAdFree ? (
              <button type="button" className="rms-btn rms-btn--ghost" style={{ width: "100%", marginTop: 14 }} onClick={() => switchPlan("free")} disabled={busy}>
                {t("settings.plan.switchToFree", "Switch to Free")}
              </button>
            ) : (
              <div style={{ marginTop: 14 }}>
                <StatusPill status="active" label={t("settings.plan.current", "current plan")} />
              </div>
            )}
          </div>

          <div className={`rms-price-card${isAdFree ? " is-featured" : ""}`}>
            <strong style={{ fontSize: 14 }}>{t("plan.adFreeName", "Ad-Free")}</strong>
            <div className="rms-price-amount">$29</div>
            <div style={{ fontSize: 13, color: "var(--rms-muted-strong)" }}>{t("settings.plan.perMonth", "per month")}</div>
            <ul className="rms-price-list">
              {PLAN_AD_FREE.map((line, i) => (
                <li key={i}>{t(`settings.plan.adFree.${i}`, line)}</li>
              ))}
              {/* Two keys, not one sentence with a link spliced into it: word
                  order moves between languages, and a hardcoded fragment either
                  side of a button cannot follow it. */}
              <li>
                {t("settings.plan.cancelAnyTime", "Cancel any time —")}{" "}
                <button
                  type="button"
                  className="rms-linkish"
                  onClick={() => navigate("/rms/settings/data")}
                >
                  {t("settings.plan.takeYourData", "take your data with you")}
                </button>
              </li>
            </ul>
            {isAdFree ? (
              <div style={{ marginTop: 14 }}>
                <StatusPill status="active" label="current plan" />
              </div>
            ) : (
              <button type="button" className="rms-btn rms-btn--primary" style={{ width: "100%", marginTop: 14 }} onClick={() => switchPlan("ad_free")} disabled={busy}>
                Remove ads — $29/mo
              </button>
            )}
          </div>
        </div>
        {isLocalBackend ? (
          <Banner tone="info">
            {t("settings.runningOnTheLocal", "Running on the local backend, so switching plans takes effect immediately with no payment step. Stripe checkout hooks in at this point when a backend is attached.")}
          </Banner>
        ) : null}
      </section>
    </>
  );
}

/**
 * The roster.
 *
 * Was: one insert that hardcoded role "staff", discarded its return value, and
 * a read-only table. The button said "Send invite" and nothing was ever sent —
 * the invitee signed up, found no business, and created a second one of their
 * own. See 20260820090002 for the half that was missing.
 */
function StaffTab({ operator, invalidate }) {
  // Held only until the request returns, then cleared -- see addStaff.
  const [password, setPassword] = useState("");

  // The shared counter login. Same treatment: never persisted in state past
  // the request that uses it.
  const [counterName, setCounterName] = useState("");
  const [counterPassword, setCounterPassword] = useState("");
  const { t, can, member: me } = useRms();
  const { rows: members } = useCollection("members");

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("staff");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [notice, setNotice] = useState(null);
  const [joinUrl, setJoinUrl] = useState(null);

  // Who on the roster has a PIN, and who is locked out. Booleans only -- the
  // function behind this returns nothing else, deliberately.
  const [pins, setPins] = useState({});
  const [settingPin, setSettingPin] = useState(false);

  const loadPins = useMemo(
    () => () => { pinService.pinStatus(operator).then((r) => setPins(r.data || {})); },
    [operator]
  );
  useEffect(loadPins, [loadPins, members]);

  const myPin = me ? pins[me.id] : null;

  const roleOptions = ROLE_OPTIONS.map((r) => ({
    value: r.value,
    label: t(r.labelKey, r.label),
  }));

  /** Every mutation runs through here so no failure can be silent again. */
  const run = async (fn, successText) => {
    setBusy(true);
    setProblem(null);
    setNotice(null);
    adEngine.recordAction();
    const result = await fn();
    setBusy(false);
    if (!result.success) {
      setProblem(result.error);
      return null;
    }
    if (successText) setNotice(successText);
    invalidate();
    return result.data;
  };

  /**
   * Make the account, then tell them it exists.
   *
   * The invitation flow it replaces could not start until an email arrived, and
   * that email has never once been delivered. This finishes before anything is
   * sent: if the message bounces or is filtered, the owner reads the password
   * out and the person works.
   */
  const addStaff = async () => {
    const address = email.trim();
    const data = await run(
      () => memberService.createStaffAccount(operator, {
        email: address,
        displayName: name,
        role,
        password,
      }),
      null
    );
    if (!data) return;

    setEmail("");
    setName("");
    setRole("staff");
    // Cleared immediately. It was only ever needed for the length of one
    // request, and a password left sitting in component state is a password
    // sitting in a React devtools panel.
    setPassword("");
    setJoinUrl(null);

    setNotice(
      data.warning
        ? { tone: "warn", text: data.warning }
        : t("staff.accountCreated", "{email} can sign in now. Tell them the password yourself — it was not emailed.", { email: address })
    );
  };

  const saveCounterLogin = async () => {
    const name = counterName.trim();
    const data = await run(
      () => memberService.setCounterLogin(operator, { loginName: name, password: counterPassword }),
      null
    );
    if (!data) return;
    setCounterPassword("");
    setNotice(
      t("counter.saved", "Anyone can now sign in as {name}. Running this again with the same name changes the password.", { name })
    );
  };

  return (
    <section className="rms-card rms-card-pad">
      <h2 className="rms-section-title">{t("settings.tab.staff", "Staff")}</h2>
      <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", marginTop: 0 }}>
        {t("settings.staffAccountsAreFree", "Staff accounts are free on every plan — there is no per-seat charge.")}
      </p>

      {problem ? <Banner tone="danger">{problem}</Banner> : null}
      {notice ? <Banner tone="ok">{notice}</Banner> : null}

      {joinUrl ? (
        <Banner tone="info">
          <div>
            <strong>{t("staff.noEmailHere", "There is no email on this backend.")}</strong>
            <p style={{ margin: "6px 0 0", fontSize: 13 }}>
              {t("staff.sendThisLink", "Send them this link yourself — it works once and expires in seven days.")}
            </p>
            <code className="rms-join-link">{joinUrl}</code>
          </div>
        </Banner>
      ) : null}

      {can.manageStaff ? (
        <>
          <div className="rms-field-grid">
            <Field label={t("settings.email", "Email")}>
              <TextInput type="email" value={email} onChange={setEmail} placeholder="colleague@example.com" />
            </Field>
            <Field label={t("settings.name", "Name")}>
              <TextInput value={name} onChange={setName} />
            </Field>
            <Field
              label={t("settings.role", "Role")}
              help={t("staff.roleHelp", "What they can reach. You can change it later.")}
            >
              <Select value={role} onChange={setRole} options={roleOptions} />
            </Field>
            <Field
              label={t("staff.tempPassword", "Password to give them")}
              help={t("staff.tempPasswordHelp", "At least 8 characters. Tell them in person — it is deliberately not emailed.")}
            >
              <input
                className="rms-input"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="off"
              />
            </Field>
          </div>
          <button
            type="button"
            className="rms-btn rms-btn--primary"
            onClick={addStaff}
            disabled={busy || !email.trim() || password.length < 8}
          >
            {busy ? t("common.working", "Working…") : t("staff.createAccount", "Create their account")}
          </button>
          <p className="rms-help" style={{ marginTop: 10 }}>
            {t("staff.createHelp", "They can sign in straight away. The email tells them to change the password and set a PIN — it never contains the password itself.")}
          </p>

          <div style={{ borderTop: "1px solid var(--rms-border)", marginTop: 22, paddingTop: 18 }}>
            <h3 className="rms-section-title">{t("counter.title", "Shared counter login")}</h3>
            <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", marginTop: 0 }}>
              {t(
                "counter.body",
                "One name and password the whole shift can use, so nobody signs in as you and a casual does not need an account of their own. It reaches the counter's work only — pricing, billing and settings stay shut to it. Each person still signs what they do with their own PIN."
              )}
            </p>

            <div className="rms-field-grid">
              <Field
                label={t("counter.name", "Counter name")}
                help={t("counter.nameHelp", "Letters and numbers. This is what they type instead of an email.")}
              >
                <TextInput value={counterName} onChange={setCounterName} placeholder="canadacars" />
              </Field>
              <Field
                label={t("counter.password", "Counter password")}
                help={t("counter.passwordHelp", "At least 8 characters. Change it whenever somebody leaves — they all know it.")}
              >
                <input
                  className="rms-input"
                  type="text"
                  value={counterPassword}
                  onChange={(e) => setCounterPassword(e.target.value)}
                  autoComplete="off"
                />
              </Field>
            </div>

            <button
              type="button"
              className="rms-btn rms-btn--ghost"
              onClick={saveCounterLogin}
              disabled={busy || counterName.trim().length < 3 || counterPassword.length < 8}
            >
              {busy ? t("common.working", "Working…") : t("counter.save", "Save the counter login")}
            </button>
          </div>
        </>
      ) : (
        <Banner tone="info">
          {t("staff.ownerOnly", "Only an owner can invite people or change what they can reach.")}
        </Banner>
      )}

      {members.length ? (
        <div className="rms-table-wrap" style={{ marginTop: 18 }}>
          <table className="rms-table">
            <thead>
              <tr>
                <th>{t("tax.rates.name", "Name")}</th>
                <th>{t("customers.email", "Email")}</th>
                <th>{t("settings.role", "Role")}</th>
                <th>{t("campaignForm.status", "Status")}</th>
                <th aria-label={t("staff.actions", "Actions")} />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const isMe = me && m.id === me.id;
                const pending = m.status === "invited";
                return (
                  <tr key={m.id}>
                    <td>
                      {m.display_name || "—"}
                      {isMe ? (
                        <span className="rms-tl-code"> {t("staff.you", "(you)")}</span>
                      ) : null}
                    </td>
                    <td style={{ fontSize: 12.5 }}>{m.email}</td>
                    <td>
                      {can.manageStaff && !isMe ? (
                        <Select
                          value={m.role}
                          onChange={(next) =>
                            run(
                              () => memberService.changeRole(m, next),
                              t("staff.roleChanged", "Role updated.")
                            )
                          }
                          options={roleOptions}
                        />
                      ) : (
                        t(`role.${m.role}`, m.role)
                      )}
                    </td>
                    <td>
                      <StatusPill
                        status={m.status === "active" ? "active" : m.status === "suspended" ? "cancelled" : "pending"}
                        label={t(`staff.status.${m.status}`, m.status)}
                      />
                    </td>
                    <td style={{ textAlign: "end", whiteSpace: "nowrap" }}>
                      {/* No actions against yourself. The database refuses to
                          leave a business without an owner anyway, but a button
                          that exists only to be rejected is not a kindness. */}
                      {can.manageStaff && !isMe ? (
                        <>
                          {pending ? (
                            <>
                              <button
                                type="button"
                                className="rms-btn rms-btn--ghost rms-btn--sm"
                                disabled={busy}
                                onClick={() =>
                                  run(
                                    () => memberService.resendInvite(operator, m),
                                    t("staff.resent", "Invitation sent again.")
                                  )
                                }
                              >
                                {t("staff.resend", "Resend")}
                              </button>{" "}
                              <button
                                type="button"
                                className="rms-btn rms-btn--ghost rms-btn--sm"
                                disabled={busy}
                                onClick={() =>
                                  run(
                                    () => memberService.revokeInvite(m),
                                    t("staff.revoked", "Invitation revoked.")
                                  )
                                }
                              >
                                {t("staff.revoke", "Revoke")}
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="rms-btn rms-btn--ghost rms-btn--sm"
                                disabled={busy}
                                onClick={() =>
                                  run(
                                    () =>
                                      memberService.setStatus(
                                        m,
                                        m.status === "suspended" ? "active" : "suspended"
                                      ),
                                    m.status === "suspended"
                                      ? t("staff.restored", "Access restored.")
                                      : t("staff.suspended", "Access suspended.")
                                  )
                                }
                              >
                                {m.status === "suspended"
                                  ? t("staff.restore", "Restore")
                                  : t("staff.suspend", "Suspend")}
                              </button>{" "}
                              {pins[m.id]?.has_pin ? (
                                <>
                                  <button
                                    type="button"
                                    className="rms-btn rms-btn--ghost rms-btn--sm"
                                    disabled={busy}
                                    title={t("staff.clearPinHelp", "They choose a new one next time they sign something. Also clears a lockout.")}
                                    onClick={() =>
                                      run(async () => {
                                        const r = await pinService.clearPin(operator, m);
                                        if (r.success) loadPins();
                                        return r;
                                      }, t("staff.pinCleared", "PIN cleared."))
                                    }
                                  >
                                    {pins[m.id]?.locked
                                      ? t("staff.unlockPin", "Unlock PIN")
                                      : t("staff.clearPin", "Clear PIN")}
                                  </button>{" "}
                                </>
                              ) : null}
                              <button
                                type="button"
                                className="rms-btn rms-btn--ghost rms-btn--sm"
                                disabled={busy}
                                onClick={() =>
                                  run(
                                    () => memberService.removeMember(m),
                                    t("staff.removed", "Removed from the team.")
                                  )
                                }
                              >
                                {t("staff.remove", "Remove")}
                              </button>
                            </>
                          )}
                        </>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Your own PIN.
          It could previously only be set by stumbling into it -- the modal turns
          into "choose one" the first time something needs a signature, and with
          nothing else on screen asking, a solo owner had no way to find it
          deliberately and no way to change it afterwards. */}
      <div className="rms-pin-card">
        <h3 className="rms-section-title" style={{ fontSize: 14 }}>
          {t("staff.yourPin", "Your PIN")}
        </h3>
        <p style={{ fontSize: 13, color: "var(--rms-muted-strong)", margin: "0 0 12px" }}>
          {myPin?.has_pin
            ? t("staff.pinSet", "Set. It signs what you do, so the log shows who did it.")
            : t("staff.pinNotSet", "Not set yet. Four digits that sign what you do, so the log shows who did it.")}
        </p>
        <button
          type="button"
          className="rms-btn rms-btn--ghost rms-btn--sm"
          onClick={() => setSettingPin(true)}
          disabled={busy}
        >
          {myPin?.has_pin ? t("staff.changePin", "Change PIN") : t("staff.setPin", "Set a PIN")}
        </button>
      </div>

      {settingPin ? (
        <PinModal
          forceSetup
          onCancel={() => setSettingPin(false)}
          onResolved={async () => {
            setSettingPin(false);
            loadPins();
            setNotice(t("staff.pinSaved", "PIN saved."));
            return {};
          }}
        />
      ) : null}

      {/* Development affordance, and labelled as one. The local backend has a
          single synthetic user who is always the owner, so without this nobody
          would ever see the staff or bookkeeper screens before they shipped. */}
      {isLocalBackend ? (
        <div className="rms-viewas">
          <Field
            label={t("staff.viewAs", "View the app as")}
            help={t("staff.viewAsHelp", "Local development only. Changes what this browser shows, not what anyone may do.")}
          >
            <Select
              value={me?.role || "owner"}
              onChange={(next) => run(() => memberService.setViewAsRole(next), null)}
              options={roleOptions}
            />
          </Field>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Data ownership.
 *
 * ReserveZoo runs one shared database rather than a Postgres project per
 * customer, because ad delivery has to be aggregated across operators to be
 * billable at all. What customers actually mean by "do I own my data?" is "can
 * I leave without losing everything?", and that is answerable without giving up
 * the revenue model: export everything, and delete everything.
 *
 * The export is required before deletion rather than merely offered. There are
 * no backups on the free tier, so it is the only safety net that exists.
 */
function DataTab({ operator, setNotice }) {
  const { t, invalidate } = useRms();
  const [busy, setBusy] = useState(false);
  const [csvTable, setCsvTable] = useState("reservations");
  const [exported, setExported] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [problem, setProblem] = useState(null);
  const [demoCount, setDemoCount] = useState(0);

  // Counted rather than assumed: the demo is deletable, so "is there any left"
  // is a question about the data, not about whether the operator is new.
  const countDemo = useCallback(() => {
    countDemoRows(operator).then(setDemoCount);
  }, [operator]);
  useEffect(countDemo, [countDemo]);

  const exportJson = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const { counts, errors } = await exportOperatorJson(operator);
      const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
      setExported(true);

      if (errors.length) {
        // A partial export with an explicit list of what is missing beats both
        // no export and a silently incomplete one.
        setProblem(
          `Exported ${total} records, but could not read: ${errors.map((e) => e.collection).join(", ")}.`
        );
      } else {
        setNotice(t("settings.data.exported", "{records, plural, one {Exported # record} other {Exported # records}} across {tables, plural, one {# table} other {# tables}}.", {
        records: total,
        tables: Object.keys(counts).length,
      }));
      }
    } catch (err) {
      setProblem(err.message);
    }
    setBusy(false);
  };

  const exportCsv = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const { rowCount } = await exportOperatorCsv(operator, csvTable);
      if (rowCount === 0) setProblem("That table has no rows yet, so there was nothing to download.");
      else setNotice(`Downloaded ${rowCount} rows.`);
    } catch (err) {
      setProblem(err.message);
    }
    setBusy(false);
  };

  const deleteAccount = async () => {
    setBusy(true);
    setProblem(null);

    if (isLocalBackend) {
      resetStore();
      window.location.href = "/rms/start";
      return;
    }

    // Scans first. Storage objects are not reachable from a SQL cascade, so
    // deleting the rows alone would leave every licence and insurance
    // certificate sitting in the bucket — making "delete everything" a lie
    // about precisely the files it matters most for.
    await removeOperatorScans(operator.id);

    // rms_operators has no DELETE policy by design, so this goes through the
    // owner-gated function rather than the table. Staff cannot invoke it.
    const { error } = await supabase.rpc("rms_delete_my_operator", { p_operator_id: operator.id });
    if (error) {
      setProblem(error.message);
      setBusy(false);
      return;
    }
    await supabase.auth.signOut();
    window.location.href = "/";
  };

  const reseed = async () => {
    setBusy(true);
    await seedOperator(operator);
    setBusy(false);
    setNotice("Demo data topped up.");
    window.location.reload();
  };

  // No confirmation step. It only ever removes rows flagged is_demo, so there
  // is nothing of the operator's to lose -- and a dialog guarding a harmless
  // action teaches people to click through the ones that are not.
  const deleteDemo = async () => {
    setBusy(true);
    setProblem(null);
    const result = await deleteDemoData(operator);
    setBusy(false);
    if (!result.success) {
      setProblem(result.error);
      return;
    }
    const { deleted, kept } = result.data;
    setNotice(
      kept
        ? t("settings.demo.removedKept", "Removed {deleted} demo records. {kept} were kept because your own bookings refer to them.", { deleted, kept })
        : t("settings.demo.removed", "Removed {deleted} demo records.", { deleted })
    );
    countDemo();
    // invalidate() rather than a page reload: the reload the neighbouring
    // "top up" button does would throw away the very notice that says what
    // happened, and the counts above are the only place the kept rows are
    // ever mentioned.
    invalidate();
  };

  const nameMatches = confirmName.trim() === operator.business_name.trim();

  return (
    <>
      <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
        <h2 className="rms-section-title">{t("settings.data.title", "Your data is yours")}</h2>
        <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", marginTop: 0 }}>
          {t("settings.everyCustomerBookingAgreement", "Every customer, booking, agreement, invoice and payment you've entered belongs to your business, not to us. Take a complete copy whenever you like — no request, no waiting, no charge, and no difference between the free and paid plans.")}
        </p>

        <div className="rms-control-row" style={{ marginTop: 16 }}>
          <div>
            <button type="button" className="rms-btn rms-btn--primary" onClick={exportJson} disabled={busy}>
              {busy ? t("common.working", "Working…") : t("settings.data.downloadAll", "Download everything (JSON)")}
            </button>
            <p className="rms-help" style={{ margin: "6px 0 0", maxWidth: 380 }}>
              {t("settings.oneFileEveryTable", "One file, every table, structure intact. This is the copy you'd hand to another system.")}
            </p>
          </div>
        </div>

        <h3 style={{ fontSize: 13, margin: "24px 0 8px" }}>{t("settings.data.oneTable", "Or one table as a spreadsheet")}</h3>
        <div className="rms-control-row">
          <Field label={t("settings.data.table", "Table")}>
            <Select
              value={csvTable}
              onChange={setCsvTable}
              options={EXPORT_COLLECTIONS.map((c) => ({ value: c.key, label: c.label }))}
            />
          </Field>
          <button type="button" className="rms-btn rms-btn--ghost" onClick={exportCsv} disabled={busy}>
            {t("settings.downloadCsv", "Download CSV")}
          </button>
        </div>
        <p className="rms-help" style={{ marginTop: 8 }}>
          {t("settings.opensInExcelOr", "Opens in Excel or Google Sheets. Amounts are written as currency rather than the integer cents we store internally.")}
        </p>

        <p className="rms-help" style={{ marginTop: 16 }}>
          {t("settings.adImpressionsAndClicks", "Ad impressions and clicks are not included — that's our record of what advertisers were billed for, not part of your business record.")}
        </p>

        {problem ? <Banner tone="warn">{problem}</Banner> : null}
      </section>

      {isLocalBackend || demoCount > 0 ? (
        <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
          <h2 className="rms-section-title">{t("settings.demo.title", "Demo data")}</h2>
          <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", marginTop: 0 }}>
            {t(
              "settings.demo.explain",
              "Your account came with a worked example — a small fleet, a few customers and three bookings — so the screens had something to show. It is labelled Demo everywhere it appears, it is not counted as your setup, and you can remove it the moment it stops being useful."
            )}
          </p>
          {isLocalBackend ? (
            <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)" }}>
              {t("settings.youReRunningOn", "You're running on the local backend — everything lives in this browser's storage and nothing is sent anywhere.")}
            </p>
          ) : null}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            {demoCount > 0 ? (
              <button type="button" className="rms-btn rms-btn--ghost" onClick={deleteDemo} disabled={busy}>
                {t("settings.demo.remove", "Delete demo data ({count})", { count: demoCount })}
              </button>
            ) : null}
            {isLocalBackend ? (
              <button type="button" className="rms-btn rms-btn--ghost" onClick={reseed} disabled={busy}>
                {t("settings.demo.topUp", "Top up demo data")}
              </button>
            ) : null}
          </div>

          <p style={{ fontSize: 12.5, color: "var(--rms-muted-strong)", marginBottom: 0 }}>
            {t("settings.demo.safe", "Deleting the demo removes only the example records. Anything you have entered stays, including a demo vehicle or customer one of your own bookings refers to.")}
          </p>
        </section>
      ) : null}

      <section className="rms-card rms-card-pad">
        <h2 className="rms-section-title">{t("settings.close.title", "Close this account")}</h2>
        {/* Three whole sentences, not one sentence with a bolded clause spliced
            into it. Each translates on its own and the middle one keeps its
            emphasis wherever a language chooses to put it. This is the most
            consequential copy in the product and it was rendering in English
            for every operator who does not read English. */}
        <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", marginTop: 0 }}>
          {t(
            "settings.close.warning",
            "Deleting removes {business} and every record attached to it — inventory, customers, reservations, agreements, invoices and payments.",
            { business: operator.business_name }
          )}{" "}
          <strong>{t("settings.close.immediate", "It happens immediately and cannot be undone.")}</strong>{" "}
          {t("settings.close.noBackup", "We keep no backup you could be restored from.")}
        </p>

        <ol style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", lineHeight: 1.7, paddingInlineStart: 18 }}>
          <li>
            {t("settings.close.step1", "Download your data above.")}{" "}
            {exported ? (
              <StatusPill status="completed" label={t("common.done", "done")} />
            ) : (
              <span style={{ color: "var(--rms-warn)" }}>{t("settings.close.notDone", "not done yet")}</span>
            )}
          </li>
          <li>{t("settings.close.step2", "Type the business name exactly to confirm.")}</li>
        </ol>

        <div style={{ maxWidth: 340 }}>
          <Field label={t("settings.close.typeName", 'Type "{business}"', { business: operator.business_name })}>
            <TextInput
              value={confirmName}
              onChange={setConfirmName}
              placeholder={operator.business_name}
              disabled={!exported}
            />
          </Field>
        </div>

        <button
          type="button"
          className="rms-btn rms-btn--danger"
          onClick={deleteAccount}
          disabled={busy || !exported || !nameMatches}
        >
          {t("settings.close.deleteButton", "Delete {business} permanently", {
            business: operator.business_name,
          })}
        </button>

        <p className="rms-help" style={{ marginTop: 12 }}>
          {t(
            "settings.close.ownerOnly",
            "Only the account owner can do this — staff accounts cannot delete the business, whatever else they have access to."
          )}{" "}
          {t("settings.close.created", "Account created {date}.", {
            date: formatDate(operator.created_at),
          })}
        </p>
      </section>
    </>
  );
}

export default Settings;
